import { Control, Controller, Path, UseControllerProps } from 'react-hook-form';
import {
  Box,
  OutlinedTextFieldProps,
  TextField,
  Typography,
} from '@mui/material';

interface PropTypes<T> extends Omit<OutlinedTextFieldProps, 'variant'> {
  name: Path<T>;
  control: Control<any>;
  defaultValue: UseControllerProps['defaultValue'];
  rules?: UseControllerProps['rules'];

  /**
   * Displays a character counter in the bottom right of the field.
   * Must also set `inputProps={{ maxLength: n }}` to display.
   * @default false
   */
  showCharCount?: boolean;
}

type CharCountType = {
  charCount: number | undefined;
  max: number | undefined;
};

const CharCount = ({ charCount, max }: CharCountType) => {
  if (max === undefined || charCount === undefined) return null;

  return (
    <Typography
      component="span"
      variant="inherit"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
      color={charCount >= max ? 'error.main' : 'text.secondary'}
      fontWeight={charCount >= max ? '500' : 'normal'}
    >
      {`${charCount ?? 0}/${max}`}
    </Typography>
  );
};

export const ControlledTextField = <T extends unknown>({
  name,
  control,
  defaultValue,
  rules,
  showCharCount,
  ...props
}: PropTypes<T>) => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue}
    rules={rules}
    render={({ field, fieldState }) => (
      <TextField
        {...field}
        error={!!fieldState.error}
        helperText={
          <Box component="span" sx={{ display: 'flex' }}>
            <Box component="span" sx={{ flex: 1 }}>
              {fieldState.error?.message || props.helperText}
            </Box>
            {showCharCount && (
              <CharCount
                charCount={field.value?.length}
                max={props.inputProps?.maxLength}
              />
            )}
          </Box>
        }
        fullWidth
        {...props}
      />
    )}
  />
);
